import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
const serif = "'Georgia', 'Times New Roman', serif";
const gold = '#D4AF37';
const ivory = '#FAF3E0';
const ivoryA = (a) => `rgba(245,245,220,${a})`;

/* Icône Tamis SVG */
const TamisIcon = ({ size = 100, swinging = false }) => (
  <svg width={size} height={size * 1.15} viewBox="0 0 100 115" fill="none" style={{
    display: 'block', margin: '0 auto',
    transformOrigin: '50% 0%',
    animation: swinging ? 'tamisSwing 3s ease-in-out infinite' : 'none',
    filter: 'drop-shadow(0 0 12px rgba(212,175,55,0.5))',
  }}>
    <defs>
      <linearGradient id="smartTamisGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F0D878"/><stop offset="40%" stopColor="#D4AF37"/><stop offset="100%" stopColor="#C49A2E"/>
      </linearGradient>
      <radialGradient id="tamisGlow" cx="50%" cy="40%" r="50%">
        <stop offset="0%" stopColor="rgba(212,175,55,0.15)"/><stop offset="100%" stopColor="rgba(212,175,55,0)"/>
      </radialGradient>
      <clipPath id="smartTamisMesh"><ellipse cx="50" cy="42" rx="31" ry="26"/></clipPath>
      <filter id="smartTamisGlow"><feGaussianBlur stdDeviation="3" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    <ellipse cx="50" cy="42" rx="42" ry="38" fill="url(#tamisGlow)"/>
    <ellipse cx="50" cy="42" rx="35" ry="30" stroke="url(#smartTamisGold)" strokeWidth="3" fill="none" filter="url(#smartTamisGlow)"/>
    <ellipse cx="50" cy="42" rx="31" ry="26" stroke="rgba(212,175,55,0.35)" strokeWidth="1" fill="none"/>
    <g clipPath="url(#smartTamisMesh)" opacity="0.45">
      {[24,32,40,48,56].map(y => <line key={`h${y}`} x1="18" y1={y} x2="82" y2={y} stroke="#D4AF37" strokeWidth="0.7"/>)}
      {[28,38,50,62,72].map(x => <line key={`v${x}`} x1={x} y1="10" x2={x} y2="74" stroke="#D4AF37" strokeWidth="0.7"/>)}
    </g>
    <ellipse cx="50" cy="30" rx="16" ry="6" fill="rgba(240,216,120,0.06)"/>
    <line x1="50" y1="72" x2="50" y2="98" stroke="url(#smartTamisGold)" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="50" cy="103" r="6" stroke="url(#smartTamisGold)" strokeWidth="2" fill="none" filter="url(#smartTamisGlow)"/>
    <circle cx="50" cy="103" r="2" fill="#D4AF37"/>
  </svg>
);

/* ── Miniature draggable ── */
const DraggableThumb = ({ photoId, token, groupCategory, isExcluded, isMobile, loadDelay, onToggle, onReject }) => {
  const [loaded, setLoaded] = React.useState(false);
  const [src, setSrc] = React.useState(null);
  const [dragging, setDragging] = React.useState(false);
  const size = isMobile ? 100 : 80;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSrc(`${API}/photos/${photoId}/thumbnail?token=${token}`);
    }, Math.min(loadDelay, 500));
    return () => clearTimeout(timer);
  }, [photoId, token, loadDelay]);

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ photoId, fromCategory: groupCategory }));
    e.dataTransfer.effectAllowed = 'move';
    setDragging(true);
  };

  const handleDragEnd = () => setDragging(false);

  return (
    <div
      data-testid={`thumb-${photoId}`}
      draggable={!isExcluded}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{
        position: 'relative', width: size, height: size,
        borderRadius: isMobile ? 12 : 8, overflow: 'hidden',
        border: dragging ? `2px solid ${gold}` : isExcluded ? '2px solid rgba(192,57,43,0.5)' : '2px solid rgba(212,175,55,0.2)',
        opacity: dragging ? 0.5 : isExcluded ? 0.35 : 1,
        transition: 'all 0.2s', cursor: isExcluded ? 'pointer' : 'grab',
        backgroundColor: 'rgba(212,175,55,0.05)',
        flexShrink: 0,
      }}
      onClick={onToggle}
      title={isExcluded ? 'Cliquez pour r\u00e9inclure' : 'Glissez vers un autre lot'}
    >
      {!loaded && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 16, height: 16, border: '1.5px solid rgba(212,175,55,0.15)', borderTop: '1.5px solid rgba(212,175,55,0.5)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      )}
      {src && (
        <img
          src={src} alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: loaded ? 'block' : 'none' }}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            if (!e.target.dataset.retried) {
              e.target.dataset.retried = '1';
              setTimeout(() => { e.target.src = `${API}/photos/${photoId}/image?token=${token}`; }, 1000);
            }
          }}
        />
      )}
      {isExcluded && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </div>
      )}
      {!isExcluded && loaded && (
        <button
          data-testid={`reject-single-${photoId}`}
          onClick={(e) => { e.stopPropagation(); onReject(); }}
          style={{
            position: 'absolute', top: 3, right: 3, width: 20, height: 20, borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.6)', border: '1px solid rgba(192,57,43,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
          }}
          title="Rejeter cette photo"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      )}
    </div>
  );
};

const SmartTamisPanel = ({ token, sasCount, onClose, onRefresh, sounds }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [groups, setGroups] = useState(null);
  const [processingGroup, setProcessingGroup] = useState(null);
  const [excludedPhotos, setExcludedPhotos] = useState({});
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const pollingRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  // Seal modal
  const [showSealModal, setShowSealModal] = useState(false);
  const [sealEcrinName, setSealEcrinName] = useState('');
  const [pendingSealGroup, setPendingSealGroup] = useState(null);

  // Rename
  const [editingCategory, setEditingCategory] = useState(null);
  const [editName, setEditName] = useState('');

  // Drag & drop
  const [dragOverCategory, setDragOverCategory] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const analyze = useCallback(async () => {
    setAnalyzing(true);
    setProgress('Lancement de l\u2019analyse...');
    try {
      const res = await axios.post(`${API}/sas/analyze`, {}, { headers });
      if (res.data.status === 'processing' && res.data.task_id) {
        const taskId = res.data.task_id;
        pollingRef.current = setInterval(async () => {
          try {
            const poll = await axios.get(`${API}/sas/analyze/status/${taskId}`, { headers });
            if (poll.data.status === 'success') {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setGroups(poll.data.groups || []);
              setExcludedPhotos({});
              setAnalyzing(false);
              sounds?.cristal?.();
            } else if (poll.data.status === 'error') {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setGroups([]);
              setAnalyzing(false);
            } else {
              setProgress(poll.data.progress || 'Analyse en cours...');
            }
          } catch { /* keep polling */ }
        }, 2000);
      } else {
        setGroups(res.data.groups || []);
        setExcludedPhotos({});
        setAnalyzing(false);
        sounds?.cristal?.();
      }
    } catch {
      setGroups([]);
      setAnalyzing(false);
    }
  }, [token]);

  const toggleExclude = (category, photoId) => {
    setExcludedPhotos(prev => {
      const cat = prev[category] || [];
      return { ...prev, [category]: cat.includes(photoId) ? cat.filter(id => id !== photoId) : [...cat, photoId] };
    });
  };

  const getActivePhotos = (group) => {
    const excluded = excludedPhotos[group.category] || [];
    return group.photo_ids.filter(id => !excluded.includes(id));
  };

  // ── SEAL: always modal ──
  const handleGroupAction = async (group, action) => {
    const activeIds = getActivePhotos(group);
    if (!activeIds.length) return;

    if (action === 'sceller') {
      setPendingSealGroup(group);
      setSealEcrinName('');
      setShowSealModal(true);
      return;
    }

    setProcessingGroup(group.category);
    try {
      const res = await axios.post(`${API}/sas/trier-lot`, { photo_ids: activeIds, action }, { headers });
      setResults(prev => [...prev, { category: group.category, action, count: res.data.count }]);
      const excluded = excludedPhotos[group.category] || [];
      if (excluded.length > 0) {
        setGroups(prev => prev.map(g => g.category === group.category
          ? { ...g, photo_ids: excluded, count: excluded.length } : g
        ));
        setExcludedPhotos(prev => ({ ...prev, [group.category]: [] }));
      } else {
        setGroups(prev => prev.filter(g => g.category !== group.category));
      }
      if (onRefresh) onRefresh();
    } catch {}
    setProcessingGroup(null);
  };

  const confirmSeal = async (withEcrin) => {
    if (!pendingSealGroup) return;
    const activeIds = getActivePhotos(pendingSealGroup);
    if (!activeIds.length) return;
    const name = withEcrin ? sealEcrinName.trim() : '';

    setShowSealModal(false);
    setProcessingGroup(pendingSealGroup.category);
    try {
      const res = await axios.post(`${API}/sas/trier-lot`, {
        photo_ids: activeIds, action: 'sceller', ecrin_name: name,
      }, { headers });
      const msg = name
        ? `${res.data.count} Tr\u00e9sor(s) scell\u00e9(s) dans l'\u00c9crin "${name}"`
        : `${res.data.count} Tr\u00e9sor(s) scell\u00e9(s) au Coffre-fort`;
      setResults(prev => [...prev, { category: pendingSealGroup.category, action: 'sceller', count: res.data.count, message: msg }]);
      const excluded = excludedPhotos[pendingSealGroup.category] || [];
      if (excluded.length > 0) {
        setGroups(prev => prev.map(g => g.category === pendingSealGroup.category
          ? { ...g, photo_ids: excluded, count: excluded.length } : g
        ));
        setExcludedPhotos(prev => ({ ...prev, [pendingSealGroup.category]: [] }));
      } else {
        setGroups(prev => prev.filter(g => g.category !== pendingSealGroup.category));
      }
      if (onRefresh) onRefresh();
    } catch {}
    setProcessingGroup(null);
    setPendingSealGroup(null);
    setSealEcrinName('');
  };

  // ── RENAME LOT ──
  const startRename = (category) => {
    setEditingCategory(category);
    setEditName(category);
  };

  const confirmRename = (oldCategory) => {
    const newName = editName.trim();
    if (!newName || newName === oldCategory) {
      setEditingCategory(null);
      return;
    }
    setGroups(prev => prev.map(g =>
      g.category === oldCategory ? { ...g, category: newName } : g
    ));
    // Also update excludedPhotos keys
    setExcludedPhotos(prev => {
      const copy = { ...prev };
      if (copy[oldCategory]) {
        copy[newName] = copy[oldCategory];
        delete copy[oldCategory];
      }
      return copy;
    });
    setEditingCategory(null);
    setEditName('');
  };

  // ── DRAG & DROP between groups ──
  const handleDrop = (targetCategory, e) => {
    e.preventDefault();
    setDragOverCategory(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      const { photoId, fromCategory } = data;
      if (fromCategory === targetCategory) return;

      setGroups(prev => prev.map(g => {
        if (g.category === fromCategory) {
          return { ...g, photo_ids: g.photo_ids.filter(id => id !== photoId), count: g.photo_ids.length - 1 };
        }
        if (g.category === targetCategory) {
          if (g.photo_ids.includes(photoId)) return g;
          return { ...g, photo_ids: [...g.photo_ids, photoId], count: g.photo_ids.length + 1 };
        }
        return g;
      }).filter(g => g.photo_ids.length > 0));

      // Clean excluded
      setExcludedPhotos(prev => {
        const copy = { ...prev };
        if (copy[fromCategory]) {
          copy[fromCategory] = copy[fromCategory].filter(id => id !== photoId);
        }
        return copy;
      });
    } catch {}
  };

  const handleDragOver = (category, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverCategory !== category) setDragOverCategory(category);
  };

  const handleDragLeave = () => setDragOverCategory(null);

  const handleRejectSingle = async (group, photoId) => {
    try {
      await axios.post(`${API}/sas/trier-lot`, { photo_ids: [photoId], action: 'rejeter' }, { headers });
      setGroups(prev => prev.map(g => {
        if (g.category !== group.category) return g;
        const newIds = g.photo_ids.filter(id => id !== photoId);
        return newIds.length > 0 ? { ...g, photo_ids: newIds, count: newIds.length } : null;
      }).filter(Boolean));
      setExcludedPhotos(prev => {
        const cat = prev[group.category] || [];
        return { ...prev, [group.category]: cat.filter(id => id !== photoId) };
      });
      if (onRefresh) onRefresh();
    } catch {}
  };

  return (
    <div data-testid="smart-tamis-panel" style={{ padding: '24px 20px', maxWidth: 800, margin: '0 auto' }}>
      <style>{`
        @keyframes tamisSwing {
          0%, 100% { transform: rotate(-8deg); }
          50% { transform: rotate(8deg); }
        }
      `}</style>

      {/* ═══ SEAL MODAL ═══ */}
      {showSealModal && (
        <div data-testid="tamis-seal-modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => { setShowSealModal(false); setPendingSealGroup(null); }}>
          <div data-testid="tamis-seal-modal" onClick={e => e.stopPropagation()} style={{ background: '#0a0a0a', border: `1.5px solid rgba(212,175,55,0.3)`, borderRadius: 16, padding: '36px 32px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 8px 60px rgba(0,0,0,0.6)' }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.5" strokeLinecap="round" style={{ margin: '0 auto 16px', display: 'block' }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <p style={{ fontFamily: serif, fontSize: 18, color: gold, marginBottom: 6 }}>
              Sceller {pendingSealGroup ? getActivePhotos(pendingSealGroup).length : 0} Tr{'\u00e9'}sor{(pendingSealGroup && getActivePhotos(pendingSealGroup).length > 1) ? 's' : ''}
            </p>
            <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', marginBottom: 4 }}>
              Lot : {pendingSealGroup?.category}
            </p>
            <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', marginBottom: 24, lineHeight: 1.6 }}>
              Quel nom pour cet {'\u00c9'}crin ?
            </p>
            <input
              data-testid="tamis-seal-ecrin-input"
              value={sealEcrinName}
              onChange={e => setSealEcrinName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && sealEcrinName.trim()) confirmSeal(true); }}
              placeholder="Ex : Mes Filles, Vacances 2024..."
              autoFocus
              style={{ fontFamily: serif, fontSize: 15, padding: '12px 18px', borderRadius: 10, border: `1.5px solid ${gold}`, background: 'rgba(0,0,0,0.5)', color: ivory, outline: 'none', width: '100%', textAlign: 'center', marginBottom: 20, letterSpacing: 0.3 }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button data-testid="tamis-seal-confirm-btn" onClick={() => confirmSeal(true)} disabled={!sealEcrinName.trim()}
                style={{ fontFamily: serif, fontSize: 14, fontWeight: 600, padding: '12px 32px', borderRadius: 10, cursor: 'pointer', backgroundColor: gold, color: '#000', border: 'none', boxShadow: '0 4px 24px rgba(212,175,55,0.35)', opacity: !sealEcrinName.trim() ? 0.5 : 1 }}>
                Sceller dans cet {'\u00c9'}crin
              </button>
              <button data-testid="tamis-seal-without-btn" onClick={() => confirmSeal(false)}
                style={{ fontFamily: serif, fontSize: 12, padding: '10px 20px', borderRadius: 8, cursor: 'pointer', color: gold, backgroundColor: 'transparent', border: `1px solid rgba(212,175,55,0.25)` }}>
                Sceller sans {'\u00c9'}crin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <p style={{ fontFamily: serif, fontSize: 22, color: gold, fontWeight: 300, marginBottom: 10, letterSpacing: 0.3 }}>
          Le Tamis Intelligent
        </p>
        <p style={{ fontFamily: serif, fontSize: 15, color: '#FAF3E0', fontWeight: 300, lineHeight: 1.7 }}>
          Bobine analyse vos {sasCount} Tr{'\u00e9'}sors en attente
          <br />et les regroupe par th{'\u00e8'}me de souvenir.
          <br /><span style={{ color: '#D4AF37' }}>Doublons d{'\u00e9'}tect{'\u00e9'}s. Archives isol{'\u00e9'}es.</span>
        </p>
      </div>

      {/* Analyze button */}
      {!groups && !analyzing && (
        <div style={{ textAlign: 'center' }}>
          <TamisIcon size={80} />
          <button
            data-testid="smart-tamis-analyze-btn"
            onClick={analyze}
            style={{
              fontFamily: serif, fontSize: 15, fontWeight: 300,
              padding: '14px 40px', borderRadius: 10, cursor: 'pointer',
              backgroundColor: gold, color: '#000', border: 'none',
              marginTop: 24, letterSpacing: 0.3,
              boxShadow: '0 4px 24px rgba(212,175,55,0.25)',
            }}
          >
            Lancer l'analyse
          </button>
        </div>
      )}

      {/* Analyzing */}
      {analyzing && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <TamisIcon size={80} swinging={true} />
          <div style={{ marginTop: 20 }}>
            <div style={{ width: 28, height: 28, border: '2px solid rgba(212,175,55,0.15)', borderTop: `2px solid ${gold}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            <p style={{ fontFamily: serif, fontSize: 14, color: '#FAF3E0', fontWeight: 300, animation: 'tamis-pulse 2s ease-in-out infinite' }}>{progress}</p>
          </div>
        </div>
      )}

      {/* Groups */}
      {groups && groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Drag hint */}
          <div style={{ textAlign: 'center', padding: '8px 16px', borderRadius: 8, background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.1)' }}>
            <p style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0', margin: 0 }}>
              Glissez une photo d'un lot {'\u00e0'} l'autre pour la d{'\u00e9'}placer. Cliquez sur le nom d'un lot pour le renommer.
            </p>
          </div>

          {groups.map((g, i) => {
            const excluded = excludedPhotos[g.category] || [];
            const activeCount = g.photo_ids.length - excluded.length;
            const isDropTarget = dragOverCategory === g.category;
            return (
              <div
                key={g.category + '-' + i}
                data-testid={`smart-group-${i}`}
                onDragOver={(e) => handleDragOver(g.category, e)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(g.category, e)}
                style={{
                  border: isDropTarget ? `2px solid ${gold}` : '1px solid rgba(212,175,55,0.15)',
                  borderRadius: 14, padding: isMobile ? '16px' : '20px',
                  background: isDropTarget ? 'rgba(212,175,55,0.08)' : 'rgba(212,175,55,0.03)',
                  transition: 'all 0.2s',
                }}
              >
                {/* Group header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    {editingCategory === g.category ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                        <input
                          data-testid={`rename-input-${i}`}
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') confirmRename(g.category); if (e.key === 'Escape') setEditingCategory(null); }}
                          onBlur={() => confirmRename(g.category)}
                          autoFocus
                          style={{ fontFamily: serif, fontSize: isMobile ? 15 : 17, color: gold, background: 'rgba(0,0,0,0.4)', border: `1px solid ${gold}`, borderRadius: 8, padding: '4px 10px', outline: 'none', width: '100%', maxWidth: 300 }}
                        />
                      </div>
                    ) : (
                      <p
                        data-testid={`group-name-${i}`}
                        onClick={() => startRename(g.category)}
                        style={{ fontFamily: serif, fontSize: isMobile ? 15 : 17, color: gold, fontWeight: 300, marginBottom: 2, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        title="Cliquez pour renommer ce lot"
                      >
                        {g.emoji} {g.category}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="2" strokeLinecap="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                      </p>
                    )}
                    {g.note && (
                      <p style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0', fontWeight: 300, fontStyle: 'italic', marginBottom: 4 }}>
                        {g.note}
                      </p>
                    )}
                    <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', fontWeight: 300 }}>
                      {activeCount} Tr{'\u00e9'}sor{activeCount > 1 ? 's' : ''} s{'\u00e9'}lectionn{'\u00e9'}{activeCount > 1 ? 's' : ''}
                      {excluded.length > 0 && <span style={{ color: 'rgba(192,57,43,0.5)' }}> ({excluded.length} retir{'\u00e9'}{excluded.length > 1 ? 's' : ''})</span>}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexShrink: 0, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                    <button
                      data-testid={`group-reject-${i}`}
                      disabled={processingGroup === g.category || activeCount === 0}
                      onClick={() => handleGroupAction(g, 'rejeter')}
                      style={{
                        fontFamily: serif, fontSize: 12, padding: isMobile ? '10px 0' : '8px 16px',
                        borderRadius: 8, cursor: 'pointer', flex: isMobile ? 1 : 'none',
                        backgroundColor: 'transparent', color: '#c0392b',
                        border: '1px solid rgba(192,57,43,0.3)',
                        opacity: (processingGroup === g.category || activeCount === 0) ? 0.4 : 1,
                      }}
                    >
                      Rejeter le lot
                    </button>
                    <button
                      data-testid={`group-accept-${i}`}
                      disabled={processingGroup === g.category || activeCount === 0}
                      onClick={() => handleGroupAction(g, 'sceller')}
                      style={{
                        fontFamily: serif, fontSize: 12, padding: isMobile ? '10px 0' : '8px 16px',
                        borderRadius: 8, cursor: 'pointer', flex: isMobile ? 1 : 'none',
                        backgroundColor: gold, color: '#000', border: 'none',
                        opacity: (processingGroup === g.category || activeCount === 0) ? 0.4 : 1,
                      }}
                    >
                      Sceller le lot
                    </button>
                  </div>
                </div>

                {/* Thumbnails grid — draggable */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fill, 80px)',
                  gap: isMobile ? 10 : 8,
                  justifyItems: isMobile ? 'center' : 'start',
                }}>
                  {g.photo_ids.map((photoId, thumbIdx) => {
                    const isExcluded = excluded.includes(photoId);
                    return (
                      <DraggableThumb
                        key={photoId}
                        photoId={photoId}
                        token={token}
                        groupCategory={g.category}
                        isExcluded={isExcluded}
                        isMobile={isMobile}
                        loadDelay={thumbIdx * 150}
                        onToggle={() => toggleExclude(g.category, photoId)}
                        onReject={() => handleRejectSingle(g, photoId)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Results */}
          {results.length > 0 && (
            <div data-testid="seal-results" style={{ textAlign: 'center', marginTop: 12, padding: '14px 16px', borderRadius: 10, background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.1)' }}>
              {results.map((r, i) => (
                <p key={i} style={{ fontFamily: serif, fontSize: 13, color: r.action === 'sceller' ? gold : 'rgba(192,57,43,0.6)', marginBottom: 4, fontWeight: 400 }}>
                  {r.message || `${r.category} : ${r.count} ${r.action === 'sceller' ? 'scell\u00e9(s)' : 'rejet\u00e9(s)'}`}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No groups */}
      {groups && groups.length === 0 && !analyzing && (
        <p style={{ fontFamily: serif, fontSize: 14, color: '#FAF3E0', textAlign: 'center', fontWeight: 300 }}>
          Pas assez de Tr{'\u00e9'}sors pour l'analyse intelligente.
        </p>
      )}

      {/* Switch to manual */}
      <div style={{ textAlign: 'center', marginTop: 28 }}>
        <button
          data-testid="smart-tamis-manual-btn"
          onClick={onClose}
          style={{
            fontFamily: serif, fontSize: 13, color: '#D4AF37',
            background: 'none', border: 'none', cursor: 'pointer',
            textDecoration: 'underline', textUnderlineOffset: 4, fontWeight: 300,
          }}
        >
          Passer au tri photo par photo
        </button>
      </div>

      <style>{`
        @keyframes tamis-pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SmartTamisPanel;
